<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue"
import { useMediaQuery } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { ChevronDown } from "@lucide/vue"
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
} from "reka-ui"
import { cn } from "@/shared/lib/utils"
import type { YearMonth } from "@/shared/lib"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet"
import MonthGrid from "./MonthGrid.vue"

const props = defineProps<{
  modelValue: YearMonth
  min?: YearMonth
  current?: YearMonth
  id?: string
  triggerAriaLabel?: string
  "data-testid"?: string
}>()

const emit = defineEmits<{
  "update:modelValue": [value: YearMonth]
}>()

defineSlots<{
  trigger?(props: { label: string; open: boolean; toggle: () => void; controls: string }): unknown
}>()

const { t, locale } = useI18n()
const isDesktop = useMediaQuery("(min-width: 768px)")
const open = ref(false)
const displayYear = ref(props.modelValue.year)

// Stable surface id for aria-controls: derived once per instance.
const surfaceId = `mp-surface-${Math.random().toString(36).slice(2, 8)}`

// On desktop, reka PopoverTrigger as-child owns the click — a noop prevents
// a double-toggle when the consumer's @click="toggle" also fires.
const noop = () => {}

watch(open, (v) => {
  if (v) {
    displayYear.value = props.modelValue.year
  }
})

const gridRef = ref<InstanceType<typeof MonthGrid>>()
const gridRefMobile = ref<InstanceType<typeof MonthGrid>>()

const triggerLabel = computed(() =>
  new Intl.DateTimeFormat(locale.value, { month: "long", year: "numeric" }).format(
    new Date(props.modelValue.year, props.modelValue.month - 1, 1),
  ),
)

function onSelect(ym: YearMonth): void {
  emit("update:modelValue", ym)
  open.value = false
}

// .prevent modifier on the template already calls e.preventDefault();
// we only need to focus the selected month cell.
function handleOpenAutoFocus(): void {
  nextTick(() => gridRef.value?.focusSelected())
}

function handleSheetOpenAutoFocus(): void {
  nextTick(() => gridRefMobile.value?.focusSelected())
}

function openSheet(): void {
  open.value = true
}

function onSheetOpenChange(v: boolean): void {
  open.value = v
}
</script>

<template>
  <!-- Desktop (≥768px): Popover -->
  <template v-if="isDesktop">
    <PopoverRoot v-model:open="open">
      <PopoverTrigger as-child>
        <!-- Custom trigger slot: reka PopoverTrigger as-child wires onClick/aria-expanded to the slot's root element.
             toggle is a noop here — reka owns the click. controls = surfaceId for aria-controls. -->
        <slot
          v-if="$slots.trigger"
          name="trigger"
          :label="triggerLabel"
          :open="open"
          :toggle="noop"
          :controls="surfaceId"
        />
        <!-- Default trigger -->
        <button
          v-else
          type="button"
          :id="id"
          :data-testid="$props['data-testid']"
          aria-haspopup="dialog"
          :aria-expanded="open"
          :aria-controls="surfaceId"
          :aria-label="triggerAriaLabel"
          :class="cn(
            'inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-2 py-1 md:min-h-0',
            'text-sm font-medium transition-colors',
            'border border-input bg-background',
            'hover:bg-accent hover:text-accent-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )"
        >
          <span>{{ triggerLabel }}</span>
          <ChevronDown
            :size="16"
            :class="cn('transition-transform duration-200', open && 'rotate-180')"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          :id="surfaceId"
          :side-offset="4"
          :class="cn(
            'z-50 w-64 rounded-md border border-border',
            'bg-popover text-popover-foreground shadow-md',
            'outline-none',
          )"
          @open-auto-focus.prevent="handleOpenAutoFocus"
        >
          <MonthGrid
            ref="gridRef"
            :model-value="modelValue"
            :min="min"
            :current="current"
            :initial-year="displayYear"
            @select="onSelect"
          />
        </PopoverContent>
      </PopoverPortal>
    </PopoverRoot>
  </template>

  <!-- Mobile (<768px): Bottom Sheet -->
  <template v-else>
    <!-- Custom trigger slot: consumer calls toggle() to open the sheet; controls = surfaceId for aria-controls. -->
    <slot
      v-if="$slots.trigger"
      name="trigger"
      :label="triggerLabel"
      :open="open"
      :toggle="openSheet"
      :controls="surfaceId"
    />
    <!-- Default trigger -->
    <button
      v-else
      type="button"
      :id="id"
      :data-testid="$props['data-testid']"
      aria-haspopup="dialog"
      :aria-expanded="open"
      :aria-controls="surfaceId"
      :aria-label="triggerAriaLabel"
      :class="cn(
        'inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-2 py-1',
        'text-sm font-medium transition-colors',
        'border border-input bg-background',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )"
      @click="openSheet"
    >
      <span>{{ triggerLabel }}</span>
      <ChevronDown
        :size="16"
        :class="cn('transition-transform duration-200', open && 'rotate-180')"
        aria-hidden="true"
      />
    </button>
    <Sheet :open="open" @update:open="onSheetOpenChange">
      <SheetContent
        :id="surfaceId"
        side="bottom"
        :show-close="true"
        :close-aria-label="t('monthPicker.close')"
        @open-auto-focus.prevent="handleSheetOpenAutoFocus"
      >
        <SheetHeader>
          <SheetTitle>{{ t("monthPicker.title") }}</SheetTitle>
        </SheetHeader>
        <MonthGrid
          ref="gridRefMobile"
          :model-value="modelValue"
          :min="min"
          :current="current"
          :initial-year="displayYear"
          @select="onSelect"
        />
      </SheetContent>
    </Sheet>
  </template>
</template>
